import { graphql, Link, useStaticQuery } from "gatsby"
import { Footer } from "@organic-return/foundation-react/src/components/Footer/Footer"
import React from "react"
import { useSiteContext } from "./SiteContext"
import {
    ESocialChannel,
    ISocialLink,
} from "@organic-return/foundation-react/src/lib"
import { IFooterProps } from "@organic-return/foundation-react/src/components/Footer/util"
import axios from "axios"
import { getRecaptchaToken } from "../../lib/utils"

const SiteFooter = () => {
    const siteContext = useSiteContext()
    const data = useStaticQuery(graphql`
    query {
      configuration: drupalSiteConfiguration {
        siteName
        footerContent1
        footerContent2
        inverted
        copyright
        footerLogo {
          alt
          width
          height
          url          
        }
        socialLinks {
          facebook {
            url
            title
          }
          instagram {
            url
            title
          }
          linkedIn {
            url
            title
          }
          twitter {
            url
            title
          }
          youTube {
            url
            title
          }
          vimeo {
            url
            title
          }
          pinterest {
            url
            title
          }
        }
        linksColumnsList: linksColumns {
            title
            linksList: links {
                url {
                    title
                    url
                }
            }
        }
        usefulLinksList: usefulLinks {
            url {
                title
                url
            }
        }
        footerSecondaryLogo {
            icon {
                url
                height
                width
                alt
            }
            url {                
                url
            }
        }
        footerBackgroundImage {
            url
            height
            width
        }
      }
    }
  `)


    let htmlCols: string[] = []
    data.configuration?.footerContent1 &&
        htmlCols.push(data.configuration.footerContent1)
    data.configuration?.footerContent2 &&
        htmlCols.push(data.configuration.footerContent2)

    let socials: ISocialLink[] = []
    data.configuration?.socialLinks?.facebook &&
        socials.push({
            channel: ESocialChannel.Facebook,
            text:
                data.configuration.socialLinks.facebook.title ||
                data.configuration.siteName + " on Facebook",
            url: data.configuration.socialLinks.facebook.url,
            target: "__blank",
        })
    data.configuration?.socialLinks?.instagram &&
        socials.push({
            channel: ESocialChannel.Instagram,
            text:
                data.configuration.socialLinks.instagram.title ||
                data.configuration.siteName + " on Instagram",
            url: data.configuration.socialLinks.instagram.url,
            target: "__blank",
        })
    data.configuration?.socialLinks?.twitter &&
        socials.push({
            channel: ESocialChannel.Twitter,
            text:
                data.configuration.socialLinks.twitter.title ||
                data.configuration.siteName + " on Twitter",
            url: data.configuration.socialLinks.twitter.url,
            target: "__blank",
        })
    data.configuration?.socialLinks?.youTube &&
        socials.push({
            channel: ESocialChannel.YouTube,
            text:
                data.configuration.socialLinks.youTube.title ||
                data.configuration.siteName + " on YouTube",
            url: data.configuration.socialLinks.youTube.url,
            target: "__blank",
        })
    data.configuration?.socialLinks?.vimeo &&
        socials.push({
            channel: ESocialChannel.Vimeo,
            text:
                data.configuration.socialLinks.vimeo.title ||
                data.configuration.siteName + " on Vimeo",
            url: data.configuration.socialLinks.vimeo.url,
            target: "__blank",
        })
    data.configuration?.socialLinks?.pinterest &&
        socials.push({
            channel: ESocialChannel.Pinterest,
            text:
                data.configuration.socialLinks.pinterest.title ||
                data.configuration.siteName + " on Pinterest",
            url: data.configuration.socialLinks.pinterest.url,
            target: "__blank",
        })
    data.configuration?.socialLinks?.linkedIn &&
        socials.push({
            channel: ESocialChannel.LinkedIn,
            text:
                data.configuration.socialLinks.linkedIn.title ||
                data.configuration.siteName + " on LinkedIn",
            url: data.configuration.socialLinks.linkedIn.url,
            target: "__blank",
        })
    let bottomText: string[] = []
    data.configuration?.copyright &&
        (bottomText = data.configuration.copyright.split("\n"))

    let linksColumns: [] = []
    data.configuration?.linksColumnsList &&
        (linksColumns = data.configuration.linksColumnsList.map((column: any) => {
            return {
                title: column.title,
                links: column.linksList && (column.linksList.map((item: any) => {
                    return {
                        text: item.url.title,
                        url: item.url.url,
                    }
                }))
            }
        }))

    let usefulLinks: [] = []
    data.configuration?.usefulLinksList &&
        (usefulLinks = data.configuration.usefulLinksList.map((item: any) => {
            return {
                text: item.url.title,
                url: item.url.url,
            }
        }))

    let secondaryLogo: any = undefined
    const configSecondaryLogo = data.configuration?.footerSecondaryLogo
    configSecondaryLogo?.icon?.url &&
        (secondaryLogo = {
            image: {
                url: configSecondaryLogo.icon.url,
                dimensions: (configSecondaryLogo.icon?.width && configSecondaryLogo.icon?.height) && [configSecondaryLogo.icon.width, configSecondaryLogo.icon.height],
                caption: configSecondaryLogo.icon?.alt
            },
            url: configSecondaryLogo.url
        })

    const footerProps: IFooterProps = {
        showAttribution: true,
        logo: {
            url: data.configuration?.footerLogo?.url,
            dimensions: [
                data.configuration?.footerLogo?.width,
                data.configuration?.footerLogo?.height,
            ],
            caption: data.configuration?.siteName,
        },
        htmlCols: htmlCols,
        linkFunc: props => (
            <Link
                to={props.href ?? ""}
                children={props.children}
                className={props.className}
                title={props.title}
                key={props.key}
                target={props.target}
            />
        ),
        className: `${data.configuration?.inverted && "inverted"}`,
        socialMediaLinks: socials,
        bottomTextLines: bottomText,
        linksGroups: linksColumns,
        utilsLinks: usefulLinks,
        backgroundImage: { url: data.configuration?.footerBackgroundImage?.url },
        secondaryLogo: secondaryLogo,
        ...siteContext.footerProps,
        formProps: {
            clickHandler: async formInput => {
                const recaptchaToken = await getRecaptchaToken("contact_us");
                await axios({
                    url: process.env.LEAD_CREATE_API_ENDPOINT,
                    method: "POST",
                    data: {
                        recaptchaToken,
                        formValues: formInput,
                        propertyInfo: {
                            createdFrom: `Contact US - Site Footer`,
                        },
                    },
                })
            },
            ...siteContext.footerProps?.formProps
        }
    }

    const Layout = siteContext.footerLayout ?? Footer

    return (
        <Layout
            {...footerProps}
        />
    )
}

export default SiteFooter
